import { Trash2 } from 'lucide-react';
import { Badge, Button, Input, Select, type SelectOption } from '../../ui';
import { ROOM_ROLES } from '../../features/rooms/roomModel';
import type { RoomMember, RoomRole } from '../../features/rooms/types';
import { PeopleInput } from '../common/PeopleInput';
import type { DirectoryPerson } from '../../features/teams/types';

const ROLE_OPTIONS: SelectOption[] = ROOM_ROLES.map((r) => ({
  value: r.value,
  label: r.label,
}));

export interface MemberRowProps {
  member: RoomMember;
  /** Alias of the room creator (rendered with a badge, non-removable). */
  createdBy: string;
  onChange: (patch: Partial<RoomMember>) => void;
  onRemove: () => void;
  /** Optional people directory to power the alias picker + name auto-fill. */
  people?: DirectoryPerson[];
}

/** Inline editor for a single room member: alias, name, role, remove. */
export function MemberRow({
  member,
  createdBy,
  onChange,
  onRemove,
  people = [],
}: MemberRowProps) {
  const isCreator = member.alias === createdBy && !!createdBy;

  // When an alias is chosen from the directory, auto-fill an empty name.
  const onAlias = (alias: string) => {
    const match = people.find(
      (p) => p.alias.toLowerCase() === alias.trim().toLowerCase(),
    );
    if (match && !member.name.trim()) {
      onChange({ alias, name: match.name });
    } else {
      onChange({ alias });
    }
  };

  return (
    <div className="grid grid-cols-1 items-end gap-3 rounded-xl border border-surface-border bg-surface-1 p-3 sm:grid-cols-[1fr_1fr_10rem_auto]">
      <PeopleInput
        label="Alias"
        value={member.alias}
        placeholder="jsmith"
        people={people}
        onChange={onAlias}
      />
      <Input
        label="Name"
        value={member.name}
        placeholder="Jane Smith"
        onChange={(e) => onChange({ name: e.target.value })}
      />
      <Select
        label="Role"
        value={member.role}
        options={ROLE_OPTIONS}
        onChange={(e) => onChange({ role: e.target.value as RoomRole })}
      />
      <div className="flex items-center gap-2 pb-0.5">
        {isCreator && <Badge tone="accent">creator</Badge>}
        <Button
          variant="ghost"
          size="sm"
          aria-label="Remove member"
          disabled={isCreator}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default MemberRow;
